import { useState, useEffect, useRef } from 'react'
import { plans, DestinationUserSearchResult } from '@/api/client'
import { Search, X, Loader2 } from 'lucide-react'

interface Props {
  projectId: string
  selectedUserId: string | null
  selectedUpn: string | null
  onSelect: (user: DestinationUserSearchResult | null) => void
  disabled?: boolean
}

/**
 * Busca usuário do destino estilo Outlook.
 * Quando vazio: mostra input de busca; ao digitar 2+ chars, dropdown com resultados.
 * Quando selecionado: mostra "chip" arredondado com X para limpar.
 */
export function DestinationUserPicker({ projectId, selectedUserId, selectedUpn, onSelect, disabled }: Props) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<DestinationUserSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      return
    }
    setSearching(true)
    const timer = setTimeout(async () => {
      try {
        const data = await plans.searchDestinationUsers(projectId, query, 8)
        setResults(data)
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [query, projectId])

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  if (selectedUserId && selectedUpn) {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-slate-300 bg-slate-50 text-xs max-w-full">
        <span className="truncate">{selectedUpn}</span>
        {!disabled && (
          <button
            type="button"
            className="text-slate-400 hover:text-red-600 shrink-0"
            onClick={() => onSelect(null)}
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
        <input
          type="text"
          className="input !pl-7 !py-1 !text-xs !min-h-0"
          placeholder="Buscar usuário do destino..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          disabled={disabled}
        />
      </div>

      {open && query.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 z-10 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {searching && (
            <div className="p-2 text-xs text-slate-500 flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Buscando...
            </div>
          )}
          {!searching && results.length === 0 && (
            <div className="p-2 text-xs text-slate-500">Nenhum usuário encontrado</div>
          )}
          {!searching && results.map(r => (
            <button
              type="button"
              key={r.id}
              disabled={r.isAlreadyLinked}
              className="w-full text-left p-2 hover:bg-slate-50 border-b last:border-0 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => { onSelect(r); setQuery(''); setOpen(false) }}
            >
              <div className="text-xs font-medium truncate">{r.displayName || r.userPrincipalName}</div>
              <div className="text-[11px] text-slate-500 truncate">
                {r.userPrincipalName}
                {r.isAlreadyLinked && <span className="ml-2 text-orange-600">(já vinculado)</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
